/**
 * SuggestedReplies — pill chips that appear under an assistant message.
 *
 * After AIKO finishes a reply, the agent emits 2-4 short follow-ups the user
 * can tap instead of typing — "Show me my top studios", "What can I do here?",
 * "Switch to AI Studio One". This primitive lays them out as a wrap-friendly
 * row of pill buttons; clicking one fires `onSelect(text)` so the consumer
 * can drop the value into the composer or send it directly.
 *
 * Default entrance is a 24ms-staggered fade-in (the chips arrive together
 * but slightly offset, the way subtitles do). Pass `motion={false}` to opt
 * out per call; `prefers-reduced-motion` forces it off regardless.
 *
 * Composition with `MessageRow`:
 *
 *   <MessageRow role="assistant" avatar={<AikoMark />}>
 *     <Markdown content={msg.text} />
 *   </MessageRow>
 *   <SuggestedReplies replies={msg.actionChips ?? []} onSelect={onChipSelect} />
 *
 * Returns `null` when `replies` is empty so consumers can lean on
 * `<SuggestedReplies replies={msg.actionChips ?? []} />` without an outer
 * length guard.
 */
import * as React from "react";
import { motion } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { Skeleton } from "../primitives/Skeleton";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { Truncate } from "./Truncate";

/* ============================================================================
 * Variants
 * ========================================================================= */

const containerVariants = cva("flex flex-wrap items-center gap-2", {
  variants: {
    align: {
      start: "justify-start",
      end: "justify-end",
    },
  },
  defaultVariants: { align: "start" },
});

const chipVariants = cva(
  cn(
    "inline-flex max-w-full items-center rounded-full border px-3 py-1.5 text-xs font-medium",
    "transition-[background-color,border-color,color,transform] duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    "active:scale-[0.98]",
  ),
  {
    variants: {
      variant: {
        default:
          "border-input bg-card text-muted-foreground shadow-sm hover:border-accent/40 hover:bg-accent/10 hover:text-foreground",
        muted:
          "border-transparent bg-muted text-muted-foreground hover:bg-accent/15 hover:text-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

/* ============================================================================
 * Component
 * ========================================================================= */

export interface SuggestedRepliesProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "onSelect">,
    VariantProps<typeof containerVariants> {
  /** Reply strings, in order. Empty array renders nothing. */
  replies: ReadonlyArray<string>;
  /** Fired when a chip is clicked. The chip's text is passed through. */
  onSelect: (text: string) => void;
  /**
   * Visual treatment.
   *   - `default` — bordered pill on `bg-card` (use under the last assistant message).
   *   - `muted`   — flatter pill on `bg-muted` (use in tighter composer rails).
   */
  variant?: "default" | "muted";
  /** Disable every chip (e.g., while the assistant is composing the next reply). */
  disabled?: boolean;
  /**
   * The follow-ups are still being generated — renders chip silhouettes of
   * mixed width in their place.
   *
   * Note this is NOT `disabled`. Disabled chips are real suggestions the user
   * may not act on yet, so they stay readable; these have no text to read.
   * Rendering nothing instead would be worse than either: the chip row is the
   * last thing under a finished answer, so its arrival would shove the whole
   * transcript up just as the user starts reading.
   */
  loading?: boolean;
  /** Silhouette count while `loading`. Default 3 — the usual 2-4 the agent emits. */
  loadingCount?: number;
  /**
   * Disable the staggered fade-in. Default true. `prefers-reduced-motion`
   * forces this off regardless.
   */
  motion?: boolean;
  /**
   * Accessible group label. Defaults to AIKO-voiced "Suggested replies".
   * Override per surface (e.g. "Quick actions" for the landing page).
   */
  "aria-label"?: string;
}

const CHIP_SKELETON_WIDTHS = ["w-32", "w-24", "w-40", "w-28"] as const;

export const SuggestedReplies = React.forwardRef<HTMLDivElement, SuggestedRepliesProps>(
  (
    {
      className,
      replies,
      onSelect,
      variant = "default",
      align,
      disabled = false,
      loading = false,
      loadingCount = 3,
      motion: enableMotion = true,
      "aria-label": ariaLabel = "Suggested replies",
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const shouldAnimate = enableMotion && reduced !== true;

    // Ahead of the empty guard: no chips YET is not the same fact as no chips
    // at all, and this component's empty answer is to vanish.
    if (loading) {
      return (
        <div
          ref={ref}
          role="group"
          aria-busy="true"
          aria-label={ariaLabel}
          className={cn(containerVariants({ align }), className)}
          {...rest}
        >
          {Array.from({ length: Math.max(1, loadingCount) }).map((_, i) => (
            // Mixed widths: equal pills read as a segmented control, which is
            // a different affordance from "some short phrases are coming".
            <Skeleton
              key={i}
              aria-hidden="true"
              animation="shimmer"
              className={cn(
                "h-8 rounded-full",
                CHIP_SKELETON_WIDTHS[i % CHIP_SKELETON_WIDTHS.length],
              )}
            />
          ))}
        </div>
      );
    }

    if (replies.length === 0) return null;

    return (
      <div
        ref={ref}
        role="group"
        aria-label={ariaLabel}
        className={cn(containerVariants({ align }), className)}
        {...rest}
      >
        {replies.map((text, i) => (
          <Chip
            key={`${i}-${text}`}
            text={text}
            index={i}
            variant={variant}
            disabled={disabled}
            animated={shouldAnimate}
            onSelect={onSelect}
          />
        ))}
      </div>
    );
  },
);
SuggestedReplies.displayName = "SuggestedReplies";

/* ============================================================================
 * Internal — single chip
 *
 * Pulled out so the static branch (animated=false) renders a plain `<button>`
 * instead of a `motion.button`. Keeps the SSR / reduced-motion path free of
 * framer machinery.
 * ========================================================================= */

interface ChipProps {
  text: string;
  index: number;
  variant: "default" | "muted";
  disabled: boolean;
  animated: boolean;
  onSelect: (text: string) => void;
}

function Chip({ text, index, variant, disabled, animated, onSelect }: ChipProps) {
  const className = chipVariants({ variant });

  if (!animated) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect(text)}
        className={className}
      >
        <Truncate as="span">{text}</Truncate>
      </button>
    );
  }

  return (
    <motion.button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(text)}
      className={className}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.22,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.04 * index,
      }}
    >
      <span className="truncate">{text}</span>
    </motion.button>
  );
}
